import { Component, effect, HostListener, inject, OnDestroy } from '@angular/core';
import { DOCUMENT, NgTemplateOutlet } from '@angular/common';
import { NavigationEnd, NavigationStart, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { LayoutService } from '../../services/layout.service';
import { UserService } from '../../services/user.service';
import { UserApiService } from '../../services/user-api.service';
import { AuthenticationService } from '../../services/authentication.service';
import { AVAILABLE_LANGUAGES } from '../../constants/languages';
import { ADMINISTRATION_NAV_ITEMS, FAQ_EXTERNAL_LINKS, FAQ_NAV_ITEMS, FAQ_SUPPLIERS_NAV_ITEMS } from '../../constants/section-navigation';
import { UserNotificationBannerComponent } from '../../../shared/components/user-notification-banner/user-notification-banner.component';
import { EnvironmentBannerComponent } from '../../../shared/components/environment-banner/environment-banner.component';
import { environment } from '../../../../environments/environment';

/** Matches legacy `BaseDataService.getUserPhotoOrDefault()` fallback image. */
const DEFAULT_AVATAR = 'assets/icons/generic-profile-picture.svg';

export interface NavItem {
  id: string;
  label: string;
  icon?: string;
  route?: string[];
  externalUrl?: string;
  requiresAdmin?: boolean;
  requiresNonSupplier?: boolean;
  requiresSupplier?: boolean;
  requiresGod?: boolean;
  children?: NavItem[];
}

function sectionItems(route: string, items: { label: string; path: string }[]): NavItem[] {
  return items.map(item => ({
    id: `${route}/${item.path}`,
    label: item.label,
    route: [route, item.path],
  }));
}

const NAV_ITEMS: NavItem[] = [
  {
    id: 'dashboard',
    label: 'Personal Dashboard',
    icon: 'pc_app_02.svg',
    route: ['/Reviews'],
  },
  {
    id: 'download-report',
    label: 'Download Report',
    icon: 'download.svg',
    route: ['/Download-Report'],
    requiresNonSupplier: true,
  },
  {
    id: 'reporting-dashboard',
    label: environment.production
      ? 'Reporting Dashboard (Hitachi log in)'
      : 'Reporting Dashboard (production, Hitachi log in)',
    icon: 'report.svg',
    externalUrl: environment.powerBiReportUrl,
    requiresNonSupplier: true,
  },
  {
    id: 'faq',
    label: 'FAQ / Roles & Responsibilities',
    icon: 'information.svg',
    route: ['/FAQ'],
    requiresNonSupplier: true,
    children: [
      ...sectionItems('/FAQ', FAQ_NAV_ITEMS),
      ...FAQ_EXTERNAL_LINKS.map(item => ({ id: 'faq/sharepoint-page', label: item.label, externalUrl: item.url })),
    ],
  },
  {
    id: 'faq-suppliers',
    label: 'FAQ / Roles & Responsibilities',
    icon: 'information.svg',
    route: ['/FAQ-Suppliers'],
    requiresSupplier: true,
    children: sectionItems('/FAQ-Suppliers', FAQ_SUPPLIERS_NAV_ITEMS),
  },
  {
    id: 'issue-reporting',
    label: 'Report Issue',
    icon: 'android.svg',
    externalUrl: 'https://hitachienergy.service-now.com/my_it?id=sc_category&sys_id=e15706fc0a0a0aa7007fc21e1ab70c2f&catalog_id=-1&spa=1',
    requiresNonSupplier: true,
  },
  {
    id: 'release-notes',
    label: 'Release Notes',
    icon: 'report.svg',
    externalUrl: 'https://hitachipowergrids.sharepoint.com/sites/QCIO/PG-SNCRP/Shared%20Documents/Forms/AllItems.aspx?newTargetListUrl=%2Fsites%2FQCIO%2FPG%2DSNCRP%2FShared%20Documents&viewpath=%2Fsites%2FQCIO%2FPG%2DSNCRP%2FShared%20Documents%2FForms%2FAllItems%2Easpx&id=%2Fsites%2FQCIO%2FPG%2DSNCRP%2FShared%20Documents%2FRelease%20Notes&viewid=8635f949%2Df0cf%2D4591%2Da4f1%2Dcaf15bcf7280',
    requiresNonSupplier: true,
  },
  {
    id: 'administration',
    label: 'Administration',
    icon: 'global_expert.svg',
    route: ['/Administration'],
    requiresAdmin: true,
    children: sectionItems('/Administration', ADMINISTRATION_NAV_ITEMS),
  },
  {
    id: 'debug',
    label: 'Debug',
    route: ['/Debug'],
    requiresGod: true,
  },
];

@Component({
  selector: 'app-default-layout',
  standalone: true,
  imports: [RouterOutlet, RouterLink, RouterLinkActive, NgTemplateOutlet, UserNotificationBannerComponent, EnvironmentBannerComponent, TranslatePipe],
  templateUrl: './default-layout.component.html',
  styleUrl: './default-layout.component.scss',
})
export class DefaultLayoutComponent implements OnDestroy {
  readonly layoutService = inject(LayoutService);
  readonly userService = inject(UserService);
  readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);
  readonly translateService = inject(TranslateService);
  private readonly userApiService = inject(UserApiService);
  private readonly authService = inject(AuthenticationService);

  readonly navItems = NAV_ITEMS;
  readonly sapQmEnabled = environment.sapQmImportEnabled;
  readonly availableLanguages: { code: string; name: string }[] = AVAILABLE_LANGUAGES;
  showUserPopover = false;
  showCreateDropdown: 'rail' | 'navbar' | null = null;
  openItemId: string | null = null;
  private isMobile = (this.document.defaultView?.innerWidth ?? 768) < 768;
  private photoCache: { photo: string; mime: string; url: string } | null = null;
  private activeCacheUrl: string | null = null;
  private readonly activeCache = new Map<string, boolean>();

  constructor() {
    effect(onCleanup => {
      if (this.layoutService.menuOpen()) {
        const originalPadding = this.document.body.style.paddingRight;
        const viewportWidth = this.document.documentElement.clientWidth;
        const scrollbarWidth = viewportWidth > 0
          ? Math.max(0, (this.document.defaultView?.innerWidth ?? 0) - viewportWidth)
          : 0;
        if (scrollbarWidth > 0) {
          const padding = parseFloat(this.document.defaultView?.getComputedStyle(this.document.body).paddingRight ?? '0') || 0;
          this.document.body.style.paddingRight = `${padding + scrollbarWidth}px`;
        }
        this.document.body.classList.add('menu--open');
        onCleanup(() => {
          this.document.body.classList.remove('menu--open');
          this.document.body.style.paddingRight = originalPadding;
        });
      } else {
        this.document.body.classList.remove('menu--open');
      }
    });
    this.router.events.pipe(takeUntilDestroyed()).subscribe(event => {
      if (event instanceof NavigationStart) {
        this.closeMenu();
      } else if (event instanceof NavigationEnd) {
        this.syncOpenItemToRoute();
      }
    });
    this.syncOpenItemToRoute();
  }

  get isSupplier(): boolean {
    return this.userService.hasRole('isSupplier');
  }

  get isAdmin(): boolean {
    return this.userService.hasRole('isAdmin');
  }

  get isGod(): boolean {
    return this.userService.user()?.isGod === true;
  }

  get showRailCreateButton(): boolean {
    return !this.isSupplier && !this.department.includes('PGHV');
  }

  /** Hides "Create incident" button when the wizard is active or the page requests it (matches legacy setNavbar(title, false)) */
  get showCreateButton(): boolean {
    return !this.isSupplier && !this.layoutService.wizardActive() && !this.layoutService.hideCreateButton();
  }

  /**
   * When the wizard is active, the navbar must start to the RIGHT of the fixed wizard sidebar.
   * This mirrors legacy: .app__navbar { left: 100% of split-wrap (60px rail + sidebar width) }
   * Returns the pixel offset for the navbar's left edge.
   */
  get navbarLeft(): string {
    const RAIL = 60;
    if (this.layoutService.wizardActive()) {
      return `${RAIL + this.layoutService.wizardSidebarWidth()}px`;
    }
    return `${RAIL}px`;
  }

  /**
   * When wizard is active, app-body must be pushed right by the sidebar width
   * so its content doesn't sit under the fixed sidebar.
   * Matches legacy: .app-split-area { width: 300px } acts as a flex spacer.
   */
  get appBodyPaddingLeft(): string {
    if (this.layoutService.wizardActive()) {
      return `${this.layoutService.wizardSidebarWidth()}px`;
    }
    return '0px';
  }

  get displayName(): string {
    return this.userService.user()?.Name ?? '';
  }

  get userEmail(): string {
    return this.userService.user()?.Email ?? '';
  }

  /** Legacy `authService.jobTitle` — shown in the user popover "aside" block alongside name/email. */
  get designation(): string {
    return this.userService.user()?.jobTitle ?? '';
  }

  /** Legacy `authService.department` — shown in the user popover "aside" block. */
  get department(): string {
    return this.userService.user()?.department ?? '';
  }

  /** Legacy `authService.company` — shown in the user popover "aside" block. */
  get company(): string {
    return this.userService.user()?.company ?? '';
  }

  /**
   * AD/Graph profile photo, rendered as a `data:` URL, matching legacy
   * `authService.profilePhoto` / `BaseDataService.getUserPhotoOrDefault()`. Falls back to the
   * generic profile picture icon when no photo (or mime type) is available on the current user.
   */
  get profilePhotoUrl(): string {
    const user = this.userService.user();
    if (user?.thumbnailPhoto && user?.thumbnailPhotoMime) {
      if (this.photoCache?.photo !== user.thumbnailPhoto || this.photoCache.mime !== user.thumbnailPhotoMime) {
        this.photoCache = {
          photo: user.thumbnailPhoto,
          mime: user.thumbnailPhotoMime,
          url: `data:${user.thumbnailPhotoMime};base64,${user.thumbnailPhoto}`,
        };
      }
      return this.photoCache.url;
    }
    return DEFAULT_AVATAR;
  }

  /**
   * Explicit role label for the user popover badge. Legacy only ever showed a plain "A" letter
   * for admins with no equivalent label for suppliers — extended here to also surface "Supplier"
   * per the current requirement, while a plain "User" role still renders no badge (matches legacy's
   * no-badge default).
   */
  get roleLabel(): 'Admin' | 'Supplier' | null {
    if (this.isAdmin) {
      return 'Admin';
    }
    if (this.isSupplier) {
      return 'Supplier';
    }
    return null;
  }

  get currentLang(): string {
    // Bug: currentLang is a Signal in @ngx-translate/core v18 — must be invoked, not typeof-checked.
    const lang = this.translateService.currentLang();
    return typeof lang === 'string' ? lang : 'en';
  }

  get menuOpen(): boolean {
    return this.layoutService.menuOpen();
  }

  get pageTitle(): string {
    return this.layoutService.pageTitle();
  }

  ngOnDestroy(): void {
    this.layoutService.closeMenu();
    this.document.body.classList.remove('menu--open');
  }

  @HostListener('window:resize')
  onResize(): void {
    const isMobile = (this.document.defaultView?.innerWidth ?? 768) < 768;
    if (isMobile !== this.isMobile) {
      this.isMobile = isMobile;
      this.syncOpenItemToRoute();
    }
  }

  private syncOpenItemToRoute(): void {
    this.openItemId = this.isMobile
      ? this.navItems.find(item => item.children && this.isItemVisible(item) && this.isNavItemActive(item))?.id ?? null
      : null;
  }

  isNavItemActive(item: NavItem): boolean {
    if (!item.route) {
      return false;
    }
    // Cached per URL: this runs from the template on every change-detection pass.
    const url = this.router.url;
    if (url !== this.activeCacheUrl) {
      this.activeCacheUrl = url;
      this.activeCache.clear();
    }
    let active = this.activeCache.get(item.id);
    if (active === undefined) {
      active = this.router.isActive(this.router.createUrlTree(item.route), {
        paths: 'subset', queryParams: 'ignored', fragment: 'ignored', matrixParams: 'ignored',
      });
      this.activeCache.set(item.id, active);
    }
    return active;
  }

  openSubmenu(item: NavItem, event: Event): void {
    if (item.children?.length) {
      event.preventDefault();
      event.stopPropagation();
      this.openItemId = item.id;
    }
  }

  get activeSubmenu(): NavItem | null {
    return this.navItems.find(item => item.id === this.openItemId && this.isItemVisible(item)) ?? null;
  }

  toggleMenu(): void {
    this.showCreateDropdown = null;
    if (this.router.url === '/Access-Denied') {
      return;
    }
    this.layoutService.toggleMenu();
  }

  closeMenu(): void {
    this.showCreateDropdown = null;
    this.layoutService.closeMenu();
  }

  navigateAndClose(route: string[]): void {
    this.layoutService.closeMenu();
    void this.router.navigate(route);
  }

  isItemVisible(item: NavItem): boolean {
    if (item.requiresNonSupplier && this.isSupplier) {
      return false;
    }
    if (item.requiresSupplier && !this.isSupplier) {
      return false;
    }
    if (item.requiresAdmin && !this.isAdmin) {
      return false;
    }
    if (item.requiresGod && !this.isGod) {
      return false;
    }
    return true;
  }

  toggleCreateDropdown(origin: 'rail' | 'navbar', event: Event): void {
    event.stopPropagation();
    this.showCreateDropdown = this.showCreateDropdown === origin ? null : origin;
  }

  createFromScratch(): void {
    this.showCreateDropdown = null;
    this.layoutService.closeMenu();
    void this.router.navigate(['/Incidents', 'Create']);
  }

  createFromSap(): void {
    this.showCreateDropdown = null;
    this.layoutService.closeMenu();
    void this.router.navigate(['/SAP-Quality-Management']);
  }

  switchLang(lang: string): void {
    this.translateService.use(lang);
    const email = this.authService.getActiveUserEmail();
    if (email) {
      this.userApiService.updateLanguage(email, lang).subscribe();
    }
  }

  toggleUserPopover(): void {
    this.showUserPopover = !this.showUserPopover;
  }

  closeUserPopover(): void {
    this.showUserPopover = false;
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    if (this.showCreateDropdown !== null) {
      this.showCreateDropdown = null;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.menuOpen) {
      this.layoutService.closeMenu();
    }
  }
}
